import { Link, Outlet, useNavigate, useParams } from 'react-router-dom'

import { useState } from 'react'
import { toast } from 'sonner'

import { CompanyMonogram, PageHeader, RoleGate, StatusPill } from '@/shared/components/common'
import { ErrorState, SkeletonRows, errorMessage } from '@/shared/components/data'
import { Button, TabsNav } from '@/shared/components/ui'
import type { TabItem } from '@/shared/components/ui'
import { useT } from '@/shared/i18n'
import type { CompanyStatus } from '@/shared/types/domain'

import { useArchiveCompany, useCompany } from './api'
import { CompanyForm } from './components/CompanyForm'

/**
 * El estado de la empresa es vocabulario del sistema —no lo escribe nadie—, así
 * que se traduce. El nombre de la empresa, que sí lo escribe alguien, no.
 */
const STATUS = {
  active: { tone: 'in_development', key: 'companies.status.active' },
  paused: { tone: 'paused', key: 'companies.status.paused' },
  archived: { tone: 'neutral', key: 'companies.status.archived' },
} as const satisfies Record<CompanyStatus, { tone: string; key: string }>

/**
 * `/companies/:id` — la ficha de la empresa.
 *
 * Accesos es el vault mixto; Documentos y Briefs son el mismo modulo de
 * documentos filtrado por `doc_type`; Tech stack NO se carga a mano: es la
 * proyeccion de los componentes de cada proyecto.
 */
export function CompanyDetailPage() {
  const t = useT()
  const { companyId = '' } = useParams()
  const navigate = useNavigate()
  const query = useCompany(companyId)
  const company = query.data
  const archivar = useArchiveCompany()
  const [editando, setEditando] = useState(false)

  const tabs: TabItem[] = [
    { to: 'resumen', label: t('companies.tab.summary') },
    {
      to: 'proyectos',
      label: t('common.label.projects'),
      count: company?.active_projects ?? undefined,
    },
    {
      to: 'contactos',
      label: t('companies.tab.contacts'),
      count: company?.contacts_count ?? undefined,
    },
    { to: 'accesos', label: t('companies.tab.accesses') },
    { to: 'documentos', label: t('companies.tab.documents') },
    { to: 'tech-stack', label: t('companies.tab.techStack') },
    { to: 'briefs', label: t('companies.tab.briefs') },
  ]

  if (query.isError) {
    return (
      <div className="p-4">
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      </div>
    )
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        actions={
          company ? (
            <RoleGate allow={['admin']}>
              <Button onClick={() => setEditando(true)}>{t('common.action.edit')}</Button>
              <Button
                variant="ghost"
                disabled={archivar.isPending || company.status === 'archived'}
                onClick={() => {
                  // Archivar saca a la empresa de todas las listas, así que se
                  // pregunta primero. El backend además la rechaza si le quedan
                  // proyectos vivos: ese mensaje se muestra tal cual viene, en
                  // vez de traducirlo a un "algo salió mal" que no dice qué
                  // hacer.
                  if (
                    !window.confirm(t('companies.action.archiveConfirm', { empresa: company.name }))
                  )
                    return
                  archivar.mutate(company.id, {
                    onSuccess: () => {
                      toast.success(t('companies.action.archived'))
                      void navigate('/companies')
                    },
                    onError: (error) =>
                      toast.error(errorMessage(error, t('companies.error.archive'))),
                  })
                }}
              >
                {t('companies.action.archive')}
              </Button>
            </RoleGate>
          ) : null
        }
        title={company?.name ?? t('common.state.none')}
        companyHue={company?.color_hue}
        companyName={company?.name}
        breadcrumb={
          <Link to="/companies" className="hover:text-text-1">
            {t('common.label.companies')}
          </Link>
        }
        description={
          company ? (
            <span className="flex flex-wrap items-center gap-2">
              <CompanyMonogram name={company.name} hue={company.color_hue} />
              <span className="num">{company.slug}</span>
              <StatusPill tone={STATUS[company.status].tone}>
                {t(STATUS[company.status].key)}
              </StatusPill>
              {company.slack_channel ? (
                <span className="num text-text-3">{company.slack_channel}</span>
              ) : (
                <span className="text-text-3">{t('companies.noSlackChannel')}</span>
              )}
            </span>
          ) : null
        }
      >
        <TabsNav items={tabs} label={t('companies.tabsLabel')} />
      </PageHeader>

      <div className="p-4">{query.isPending ? <SkeletonRows rows={6} /> : <Outlet />}</div>

      {/*
        La ficha entera, incluido el estado: "Activa" era un cartel y ahora es
        algo que se puede cambiar.

        Se monta **recién al abrir** y no siempre-oculto: el formulario toma sus
        valores iniciales en el primer render, y montado desde el arranque los
        tomaba con la empresa todavía sin cargar — los campos quedaban vacíos y
        "Guardar" deshabilitado para siempre.
      */}
      {editando && company ? (
        <CompanyForm key={company.id} open company={company} onClose={() => setEditando(false)} />
      ) : null}
    </div>
  )
}
